import { useQuery } from '@tanstack/react-query';

import {
  campaignsService,
  FetchCampaignCandidatesRequest,
  FetchCampaignCandidatesResponse,
} from '@aslanpay/portal-common-lib';

/** The endpoint serves at most this many at once, so a long list is gathered a page at a time. */
const PAGE_SIZE = 100;

/** A ceiling on the gathering, so a company of thousands cannot turn one dropdown into endless calls. */
const MAX_PAGES = 20;

/**
 * The colleagues a campaign will accept as its winner, gathered whole.
 * <p>
 * Asked of the campaign rather than worked out from an employee search: where a winner has to have been
 * nominated these are the nominees; where one may be picked without nominations the campaign's own setting
 * decides. The pages are bounded so one call never reads a whole company, and the rest are fetched together
 * once the first says how many there are.
 */
const useFetchWinnerCandidates = (
  campaignId: string | undefined,
  params?: FetchCampaignCandidatesRequest,
  enabled = true
) => {
  return useQuery<FetchCampaignCandidatesResponse, Error>(
    ['fetchWinnerCandidates', campaignId, params],
    async () => {
      const ask = (page: number) =>
        campaignsService.fetchWinnerCandidates(campaignId as string, {
          ...params,
          page,
          size: PAGE_SIZE,
        });

      const first = await ask(0);
      const pages = Math.min(Math.ceil(first.total / PAGE_SIZE), MAX_PAGES);

      if (pages <= 1) {
        return first;
      }

      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_unused, index) => ask(index + 1))
      );

      return { ...first, list: rest.reduce((all, page) => all.concat(page.list), [...first.list]) };
    },
    // The list is searched as it is typed, so the previous answer stays on screen while the next one is
    // fetched rather than the dropdown emptying between keystrokes.
    { enabled: enabled && !!campaignId, retry: false, keepPreviousData: true }
  );
};

export default useFetchWinnerCandidates;
